import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Icon,
	IconButton,
	ListSubheader,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useProfile } from "../../../context/ProfileContext";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../enums/profiles";
import { useToast } from "../../../shared/hooks";
import { useDebounce } from "../../hooks";
import { contractService } from "../../services/api/contract/contractService";
import { IReportCreate, reportsServices } from "../../services/api/reports/reportsServices";
import { validationPermission } from "../../utils/validationPermission";
import { ToastContent } from "../ToastContent";
import CloseIcon from "@mui/icons-material/Close";

interface IRequestReport {
	isCloseModal: () => void;
	onclickCloseModal: () => void;
	isOpenModal: boolean;
	isRefresh?: () => void;
	onReportCreated?: () => void;
}

type TAutoCompletContract = {
	id: string;
	label: string;
};

export const ModalRequestReport: React.FC<IRequestReport> = ({ isCloseModal, onclickCloseModal, isOpenModal, isRefresh, onReportCreated }) => {
	const { permissions } = useProfile();
	const [reportType, setReportType] = useState("report.analytics");
	const [periodPreset, setPeriodPreset] = useState<
		"current_month" | "previous_month" | "next_month" | "last_7_days" | "last_30_days" | "last_3_months" | "custom"
	>("current_month");

	const formatLocalISODate = (date: Date): string => {
		const timezoneOffsetMs = date.getTimezoneOffset() * 60_000;
		return new Date(date.getTime() - timezoneOffsetMs).toISOString().split("T")[0];
	};

	const getFirstDayOfMonth = (date: Date): string => {
		return formatLocalISODate(new Date(date.getFullYear(), date.getMonth(), 1));
	};

	const getLastDayOfMonth = (date: Date): string => {
		return formatLocalISODate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
	};

	const computePresetRange = (
		preset: "current_month" | "previous_month" | "next_month" | "last_7_days" | "last_30_days" | "last_3_months"
	): { start: string; end: string } => {
		const today = new Date();
		if (preset === "current_month") {
			return { start: getFirstDayOfMonth(today), end: formatLocalISODate(today) };
		}
		if (preset === "previous_month") {
			const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			return { start: getFirstDayOfMonth(prev), end: getLastDayOfMonth(prev) };
		}
		if (preset === "next_month") {
			const next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
			return { start: getFirstDayOfMonth(next), end: getLastDayOfMonth(next) };
		}
		if (preset === "last_7_days") {
			const start = new Date(today);
			start.setDate(start.getDate() - 6);
			return { start: formatLocalISODate(start), end: formatLocalISODate(today) };
		}
		if (preset === "last_30_days") {
			const start = new Date(today);
			start.setDate(start.getDate() - 29);
			return { start: formatLocalISODate(start), end: formatLocalISODate(today) };
		}

		// últimos 3 meses: do 1º dia do mês de 2 meses atrás até hoje
		const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
		return { start: formatLocalISODate(start), end: formatLocalISODate(today) };
	};

	const [reportBegin, setReportBegin] = useState<string>(() => getFirstDayOfMonth(new Date()));
	const [reportEnd, setReportEnd] = useState(() => formatLocalISODate(new Date()));

	const formatBRDate = (iso: string): string => {
		if (!iso) return "";
		const parsed = new Date(`${iso}T00:00:00`);
		if (Number.isNaN(parsed.getTime())) return iso;
		return parsed.toLocaleDateString("pt-BR");
	};

	const periodRangeLabel = useMemo(() => {
		if (!reportBegin || !reportEnd) return "";
		return `${formatBRDate(reportBegin)} a ${formatBRDate(reportEnd)}`;
	}, [reportBegin, reportEnd]);
	const toastHandler = useToast();

	const [options, setOptions] = useState<TAutoCompletContract[]>([]);
	const { debounce } = useDebounce();
	const [isLoading, setIsLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

	useEffect(() => {
		debounce(() => {
			contractService.getAll(1, searchText, "", "").then((result) => {
				setIsLoading(false);
				if (!(result instanceof Error)) {
					setOptions(result.results.map((contract) => ({ id: contract.id, label: contract.title })));
				}
			});
		});
	}, [searchText]);

	const autoCompletSelectedOption = useMemo(() => {
		if (!selectedId) return null;
		return options.find((opcao) => opcao.id === selectedId) || null;
	}, [selectedId, options]);

	type ReportOption = { value: string; label: string; requiredAction: PermissionActionsEnum };

	const hasReportPermission = (requiredAction: PermissionActionsEnum): boolean => {
		const canAdd = validationPermission(permissions, PermissionRolesEnum.ReportsOptions, PermissionActionsEnum.Add);
		const canSpecific = validationPermission(permissions, PermissionRolesEnum.ReportsOptions, requiredAction);
		return canAdd && canSpecific;
	};

	const reportGroups = useMemo(() => {
		const groups: { label: string; options: ReportOption[] }[] = [
			{
				label: "Análises e garantias",
				options: [
					{ value: "report.analytics", label: "Análises", requiredAction: PermissionActionsEnum.ReportAnalysis },
					{ value: "report.serasa", label: "Análises Serasa", requiredAction: PermissionActionsEnum.ReportAnalysis },
					{ value: "report.revision", label: "Análises Revertidas", requiredAction: PermissionActionsEnum.ReportAnalysis },
					{ value: "report.proposal", label: "Garantias", requiredAction: PermissionActionsEnum.ReportProposals },
				],
			},
			{
				label: "Repasse",
				options: [
					{ value: "report.setup", label: "Taxa Setup", requiredAction: PermissionActionsEnum.ReportFinancial },
					{ value: "report.comission", label: "Premiação/Bonificação", requiredAction: PermissionActionsEnum.ReportFinancial },
				],
			},
			{
				label: "Financeiro",
				options: [
					{ value: "report.financial_billet", label: "Boletos", requiredAction: PermissionActionsEnum.ReportFinancial },
					{
						value: "report.financial_billet_analytical",
						label: "Boletos Analitico",
						requiredAction: PermissionActionsEnum.ReportFinancial,
					},
					{ value: "report.nfe_billet", label: "Notas x Boletos", requiredAction: PermissionActionsEnum.ReportFinancial },
					{ value: "report.financial_invoicing", label: "Faturamento", requiredAction: PermissionActionsEnum.ReportFinancial },
					{
						value: "report.financial_invoicing_analytical",
						label: "Faturamento Analítico",
						requiredAction: PermissionActionsEnum.ReportFinancial,
					},
					{ value: "report.adjustment", label: "Reajustes", requiredAction: PermissionActionsEnum.ReportFinancial },
				],
			},
			{
				label: "Sinistros",
				options: [{ value: "report.sinister", label: "Relatório de Sinistros", requiredAction: PermissionActionsEnum.ReportSinister }],
			},
			{
				label: "Cadastro",
				options: [
					{ value: "report.client", label: "Cadastro de Imobiliarias", requiredAction: PermissionActionsEnum.ReportClient },
					{ value: "report.client_commercial", label: "Comercial imobiliarias", requiredAction: PermissionActionsEnum.ReportClient },
				],
			},
			{
				label: "Outros",
				options: [
					{ value: "report.contract", label: "Contratos", requiredAction: PermissionActionsEnum.ReportAdmin },
					{ value: "report.nfse", label: "Notas Fiscais", requiredAction: PermissionActionsEnum.ReportAdmin },
				],
			},
		];

		return groups
			.map((group) => ({
				...group,
				options: group.options.filter((opt) => hasReportPermission(opt.requiredAction)),
			}))
			.filter((group) => group.options.length > 0);
	}, [permissions]);

	const availableReportKeys = useMemo(() => reportGroups.flatMap((g) => g.options.map((o) => o.value)), [reportGroups]);

	useEffect(() => {
		if (!isOpenModal) return;
		if (periodPreset === "custom") return;
		const { start, end } = computePresetRange(periodPreset);
		setReportBegin(start);
		setReportEnd(end);
		setSelectedId(undefined);
	}, [isOpenModal, periodPreset]);

	useEffect(() => {
		if (!isOpenModal) return;
		if (availableReportKeys.length === 0) return;
		if (!availableReportKeys.includes(reportType)) {
			setReportType(availableReportKeys[0]);
			setSelectedId(undefined);
		}
	}, [isOpenModal, availableReportKeys, reportType]);

	const isCurrentReportTypeAllowed = useMemo(() => {
		if (availableReportKeys.length === 0) return false;
		return availableReportKeys.includes(reportType);
	}, [availableReportKeys, reportType]);

	const handleReportCreate = async (dados: IReportCreate) => {
		if (!isCurrentReportTypeAllowed) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Sem permissão"} content={"Você não tem permissão para solicitar este relatório."} />,
			});
			return;
		}
		reportsServices.createReport(dados).then((response) => {
			if (response instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={"Erro"} content={"Erro ao solicitar relatório"} />,
				});
			} else {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: <ToastContent title={"Confirmação"} content={"Relatório solicitado com sucesso!"} />,
				});
				onReportCreated?.();
				onclickCloseModal();
			}
		});
	};

	return (
		<Dialog open={isOpenModal} onClose={isCloseModal} fullWidth maxWidth="md">
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h5" fontWeight={300} color="primary.main">
					Solicitar Relatório
				</Typography>
				<IconButton onClick={onclickCloseModal} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 3, py: 2 }}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							label="Tipo de Relatório"
							select
							size="small"
							value={reportType}
							onChange={(e) => {
								setReportType(e.target.value as string);
								setSelectedId(undefined);
							}}
							disabled={availableReportKeys.length === 0}
						>
							{availableReportKeys.length === 0 && (
								<MenuItem value={""} disabled>
									Nenhum relatório disponível
								</MenuItem>
							)}

							{reportGroups
								.flatMap((group, groupIndex) => {
									const children = [
										<ListSubheader
											key={`group-${group.label}`}
											disableSticky
											sx={{
												fontWeight: 600,
												lineHeight: 1.8,
												bgcolor: "action.hover",
												color: "text.primary",
												px: 2,
												py: 0.5,
											}}
										>
											{group.label}
										</ListSubheader>,
										...group.options.map((opt) => (
											<MenuItem key={opt.value} value={opt.value}>
												{opt.label}
											</MenuItem>
										)),
									];

									if (groupIndex < reportGroups.length - 1) {
										children.push(<Divider key={`divider-${group.label}`} />);
									}

									return children;
								})}
						</TextField>
					</Grid>

					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							label="Período"
							select
							size="small"
							value={periodPreset}
							onChange={(e) => setPeriodPreset(e.target.value as any)}
						>
							<MenuItem value="current_month">Mês atual</MenuItem>
							<MenuItem value="previous_month">Mês anterior</MenuItem>
							<MenuItem value="next_month">Próximo mês</MenuItem>
							<MenuItem value="last_7_days">Últimos 7 dias</MenuItem>
							<MenuItem value="last_30_days">Últimos 30 dias</MenuItem>
							<MenuItem value="last_3_months">Últimos 3 meses</MenuItem>
							<MenuItem value="custom">Outros</MenuItem>
						</TextField>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
							Intervalo selecionado: {periodRangeLabel}
						</Typography>
					</Grid>

					{periodPreset === "custom" && (
						<>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Data Inicial"
									type="date"
									size="small"
									value={reportBegin}
									onChange={(e) => setReportBegin(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Data Final"
									type="date"
									size="small"
									value={reportEnd}
									onChange={(e) => setReportEnd(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
						</>
					)}

					{reportType === "report.contract" && isCurrentReportTypeAllowed && (
						<Grid item xs={12}>
							<Autocomplete
								options={options}
								loading={isLoading}
								value={autoCompletSelectedOption}
								onInputChange={(_, value) => setSearchText(value)}
								onChange={(_, newValue) => {
									setSelectedId(newValue?.id);
									setSearchText("");
								}}
								renderInput={(params) => <TextField {...params} size="small" label="Contrato" fullWidth />}
							/>
						</Grid>
					)}
				</Grid>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button color="inherit" variant="contained" startIcon={<Icon>close</Icon>} onClick={onclickCloseModal}>
					Fechar
				</Button>
				<Button
					color="primary"
					variant="contained"
					startIcon={<Icon>check</Icon>}
					disabled={!isCurrentReportTypeAllowed}
					onClick={() =>
						handleReportCreate({
							report_key: reportType,
							date_start: reportBegin,
							date_end: reportEnd,
							manager_id: selectedId,
						})
					}
				>
					Solicitar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
